import { useCallback, useMemo } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { TimeSlotType } from 'shared-types/SpaPackage';
import type { TimeslotChip } from 'voyager';

import { formatTime } from '../SpaDayBooking.helpers';
import { SpaPackageFormValues } from '../../SpaPackageForm.schema';

type UseArrivalTimeParams = {
  form: UseFormReturn<SpaPackageFormValues>;
  timeslots: Record<string, TimeSlotType[]> | undefined;
  selectedDateString: string;
  selectedTime: string | undefined;
  hideArrivalTimes: boolean;
};

export function useArrivalTime({
  form,
  timeslots,
  selectedDateString,
  selectedTime,
  hideArrivalTimes,
}: UseArrivalTimeParams) {
  const selectedDateTimeSlots = timeslots?.[selectedDateString];

  const arrivalTimeChips: TimeslotChip[] | undefined = useMemo(() => {
    if (hideArrivalTimes || !selectedDateTimeSlots) return undefined;
    return selectedDateTimeSlots.map((slot) => ({
      value: slot.startTime,
      label: formatTime(slot.startTime),
      price: slot.price.amount,
      currencyCode: slot.price.currencyCode,
    }));
  }, [hideArrivalTimes, selectedDateTimeSlots]);

  const handleArrivalTimeSelect = useCallback(
    (value: string) => {
      const slot = selectedDateTimeSlots?.find((s) => s.startTime === value);
      if (slot) {
        form.setValue('arrivalTime', slot);
      }
    },
    [form, selectedDateTimeSlots]
  );

  const endTime = selectedDateTimeSlots?.find((slot) => slot.startTime === selectedTime)?.endTime;

  return {
    selectedDateTimeSlots,
    arrivalTimeChips,
    handleArrivalTimeSelect,
    endTime,
  };
}
